import * as api from '@gitbook/api-client';
import { isActiveStatus, type OrganizationBilling } from '@gitbook/app-database';
import { ensureIsDefined } from '@gitbook/util-ts';

export type NewProduct =
    | api.BillingProduct.Community2024
    | api.BillingProduct.Free2024
    | api.BillingProduct.Plus2024
    | api.BillingProduct.Pro2024
    | api.BillingProduct.Enterprise2024
    | api.BillingProduct.Internal;

export type OldProduct = Exclude<api.BillingProduct, NewProduct>;

export type PricingProductDefinition<T extends OldProduct | NewProduct> = {
    key: T;
    title: string;
    description: string;
};

/**
 * Below we have two arrays of products. The old ones and the new ones.
 *
 * The order matters: products should be sorted from lowest to highest
 * and the product positions should match between the two lists.
 *
 * This is because we use indexes to determine whether we are upgrading or
 * downgrading, when switching from an product to another.
 */
export const OLD_PRICING_PRODUCTS: readonly PricingProductDefinition<OldProduct>[] = [
    {
        key: api.BillingProduct.Free,
        title: 'Personal',
        description: 'Perfect for individuals',
    },
    {
        key: api.BillingProduct.Plus,
        title: 'Plus',
        description: 'For small teams that want to collaborate',
    },
    {
        key: api.BillingProduct.Pro,
        title: 'Pro',
        description: 'For teams building docs at scale',
    },
] as const;

export const NEW_PRICING_PRODUCTS: readonly PricingProductDefinition<NewProduct>[] = [
    {
        key: api.BillingProduct.Free2024,
        title: 'Free',
        description: 'Perfect for individuals',
    },
    {
        key: api.BillingProduct.Community2024,
        title: 'Community',
        description:
            'Everything you need to get started on your own or with your teammates on your community project',
    },
    // Plus2024 is deprecated but we'll keep it here for existing Plus customers, until phased out.
    // The plans table and EOT flow will hide it as needed.
    {
        key: api.BillingProduct.Plus2024,
        title: 'Plus',
        description: 'For small teams that want to collaborate',
    },
    {
        key: api.BillingProduct.Pro2024,
        title: 'Pro',
        description: 'For teams building docs at scale',
    },
    {
        key: api.BillingProduct.Enterprise2024,
        title: 'Enterprise',
        description: 'For companies seeking enterprise-grade workflows, controls, integrations',
    },
    {
        key: api.BillingProduct.Internal,
        title: 'Internal',
        description: 'For internal use only - same access level as Enterprise',
    },
] as const;

const NEW_PRICING_PRODUCT_BY_KEYS = NEW_PRICING_PRODUCTS.reduce<
    Record<string, PricingProductDefinition<NewProduct>>
>((byKey, product) => {
    byKey[product.key] = product;
    return byKey;
}, {});

/**
 * Get the index of a product in the list of products.
 */
export function getProductIndex(product: api.BillingProduct): number {
    let index = NEW_PRICING_PRODUCTS.findIndex((def) => def.key === product);
    if (index === -1) {
        index = OLD_PRICING_PRODUCTS.findIndex((def) => def.key === product);
    }
    return index;
}

/**
 * Check if the given key is a new product key.
 */
export function checkIsNewProductKey(key: string): key is NewProduct {
    return Boolean(NEW_PRICING_PRODUCT_BY_KEYS[key]);
}

/**
 * Check if the given product is a new product definition.
 */
export function checkIsNewProductDefinition(
    product: PricingProductDefinition<api.BillingProduct>
): product is PricingProductDefinition<NewProduct> {
    return NEW_PRICING_PRODUCTS.some((p) => p.key === product.key);
}

/**
 * Check if the given key is an old product key.
 */
export function checkIsOldProductKey(key: string): key is OldProduct {
    return !checkIsNewProductKey(key);
}

/**
 * Check if the given product is a new product definition.
 */
export function checkIsOldProductDefinition(
    product: PricingProductDefinition<api.BillingProduct>
): product is PricingProductDefinition<OldProduct> {
    return checkIsOldProductKey(product.key);
}

/**
 * Get a specific product in either the old or the new set
 */
export function assertProductDefinition(key: string): PricingProductDefinition<api.BillingProduct> {
    const definition = getProductDefinition(key);
    return ensureIsDefined(definition, `Product ${key} not found`);
}

/**
 * Get a specific product in either the old or the new set
 */
export function getProductDefinition(
    key: string
): PricingProductDefinition<api.BillingProduct> | null {
    if (!checkIsProductKey(key)) {
        return null;
    }
    const definition =
        OLD_PRICING_PRODUCTS.find((product) => product.key === key) ||
        NEW_PRICING_PRODUCTS.find((product) => product.key === key);
    return (
        definition || {
            key,
            title: key,
            description: key,
        }
    );
}

/**
 * Determines whether the given product key is internal.
 */
export function checkIsInternalProductKey(key: string): key is api.BillingProduct.Internal {
    return key === api.BillingProduct.Internal;
}

/**
 * Check if the given product key is a valid product key.
 */
export function checkIsProductKey(input: string): input is api.BillingProduct {
    return (
        checkIsCommunityProductKey(input) ||
        Object.values(api.BillingProduct).includes(input as api.BillingProduct)
    );
}

/**
 * Determines whether the given product key is a community plan.
 */
export function checkIsCommunityProductKey(key: string): key is api.BillingProduct.Community2024 {
    return key === api.BillingProduct.Community2024;
}

/**
 * Determines whether the given product key is a free plan.
 */
export function checkIsFreeProductKey(
    key: string
): key is api.BillingProduct.Free | api.BillingProduct.Free2024 {
    return key === api.BillingProduct.Free || key === api.BillingProduct.Free2024;
}

/**
 * Determines whether the given product key is an enterprise plan.
 *
 * IMPORTANT:
 * Use `checkIsActiveUnrestrictedBilling` instead of this function
 * to verify billing.product. It ensures the billing record is active, as
 * Enterprise orgs with canceled subscriptions are effectively on Free.
 *
 * If you have an organization object returned by the API (api.Organization),
 * it's safe to use `checkIsEnterpriseProductKey(organization.plan)` because the API encoder
 * will not return an Enterprise plan if the subscription is not active.
 */
export function checkIsEnterpriseProductKey(key: string): key is api.BillingProduct.Enterprise2024 {
    return key === api.BillingProduct.Enterprise2024;
}

/**
 * Determines whether the given product key is an unrestricted product.
 * Unrestricted products have access to all features and sites but they are not self-serve.
 */
export function checkIsUnrestrictedProductKey(
    key: string
): key is Exclude<
    api.BillingProduct,
    | api.BillingProduct.Enterprise2024
    | api.BillingProduct.Community2024
    | api.BillingProduct.Internal
> {
    return (
        checkIsCommunityProductKey(key) ||
        checkIsEnterpriseProductKey(key) ||
        checkIsInternalProductKey(key)
    );
}

/**
 * Determines whether the given billing is an active unrestricted org
 */
export function checkIsActiveUnrestrictedBilling(
    billing: OrganizationBilling
): billing is OrganizationBilling & {
    status: 'trialing' | 'active' | 'past_due';
    product:
        | api.BillingProduct.Enterprise2024
        | api.BillingProduct.Community2024
        | api.BillingProduct.Internal;
} {
    return isActiveStatus(billing.status) && checkIsUnrestrictedProductKey(billing.product);
}

/**
 * Check if the two given products and intervals are equivalent.
 */
export function areEquivalentProducts(
    from: {
        product: api.BillingProduct;
        interval: api.BillingInterval;
    },
    to: {
        product: api.BillingProduct;
        interval: api.BillingInterval;
    }
): boolean {
    if (from.product !== to.product) {
        return false;
    }
    if (checkIsFreeProductKey(from.product)) {
        return true;
    }
    return from.interval === to.interval;
}

/**
 * Get the product definition for the billing product.
 */
export function getBillingProduct(billing: OrganizationBilling) {
    if (isActiveStatus(billing.status)) {
        return billing.product;
    }

    // When the subscription is not active, the current plan is Free/Personal by default
    return getFreeProductForBilling(billing.product);
}

/**
 * Get the free product for the given billing.
 */
export function getFreeProductForBilling(product: api.BillingProduct) {
    return checkIsNewProductKey(product) ? api.BillingProduct.Free2024 : api.BillingProduct.Free;
}

/**
 * Determines whether the given product is legacy.
 * Legacy products may be displayed to existing customers to self-serve out of them.
 * It's not possible to upgrade or downgrade to a legacy product.
 * Legacy products can be either old or new pricing.
 */
export function isLegacyProduct(plan: api.BillingProduct): boolean {
    return (
        plan === api.BillingProduct.Free ||
        plan === api.BillingProduct.Plus ||
        plan === api.BillingProduct.Pro ||
        plan === api.BillingProduct.Plus2024
    );
}

/**
 * Determines whether the given product is a deprecated or old pricing plan.
 */
export function checkIsLegacyProductKey(plan: api.BillingProduct): boolean {
    return isLegacyProduct(plan) || checkIsOldProductKey(plan);
}
